import React, { useCallback, useMemo, useState } from "react"

import { Button, TagSelect, type TagSelectOption } from "@coval/ui"
import { Plus, Tag as TagIcon } from "lucide-react"

import { getDefaultTagColor } from "../constants/tag-colors"
import { MAX_TAGS } from "../constants/tag-constraints"
import { useUpdateOrganizationTagColor } from "../routes/useUpdateOrganizationTagColor"
import type { TagSuggestion } from "../types"
import { buildTagColorMap } from "../utils/tagCatalog"
import { normalizeTag, normalizeTags } from "../utils/tagNormalization"
import { TagChip } from "./TagChip"
import { TagColorSwatchPicker } from "./TagColorSwatchPicker"
import { TagNameEditor } from "./TagNameEditor"

interface TagEditorProps {
  tags: string[]
  onTagsChange: (tags: string[]) => void
  suggestions?: TagSuggestion[]
  inputVariant?: "icon" | "tag" | "button"
  maxTags?: number
  maxVisibleTags?: number
  maxOverflowDots?: number
  showInput?: boolean
  allowRemove?: boolean
  allowColorEdit?: boolean
  allowNameEdit?: boolean
  wrap?: boolean
  collapseOverflow?: boolean
  /** Trigger style for the `collapseOverflow` control. */
  triggerVariant?: "secondary" | "ghost"
  /** Renders the `collapseOverflow` control as a labelled button. */
  triggerLabel?: string
  /** Shows the current tags but blocks every edit. */
  disabled?: boolean
  className?: string
}

/**
 * Tag assignment across every surface that carries tags.
 *
 * Two presentations share one props API. `collapseOverflow` surfaces (the
 * table cells) render nothing but the `TagSelect` control, whose trigger
 * carries a colored glyph per tag. Everything else renders readable chips
 * with the control alongside as the way to add.
 *
 * Renaming and recoloring belong to the organization rather than to any one
 * tagged record, so they are wired into `TagSelect`'s per-option slots here in
 * the app layer where the mutations live: the option's dot recolors the tag,
 * and its label renames it in place.
 */
export const TagEditor: React.FC<TagEditorProps> = ({
  tags,
  onTagsChange,
  suggestions = [],
  inputVariant = "icon",
  maxTags = MAX_TAGS,
  maxVisibleTags,
  maxOverflowDots = 3,
  showInput = true,
  allowRemove = showInput,
  allowColorEdit = false,
  allowNameEdit = false,
  wrap = true,
  collapseOverflow = false,
  triggerVariant,
  triggerLabel,
  disabled = false,
  className,
}) => {
  // A slot popover portals outside the menu, so the menu has to be told to
  // stay open while one is showing.
  const [openLayerKey, setOpenLayerKey] = useState<string | null>(null)

  const currentTags = useMemo(
    () => normalizeTags(tags, maxTags),
    [maxTags, tags],
  )
  const tagColorMap = useMemo(
    () => buildTagColorMap(suggestions),
    [suggestions],
  )
  const tagIdsByName = useMemo(() => {
    const map: Record<string, string> = {}
    for (const suggestion of suggestions) {
      if (suggestion.id) {
        map[normalizeTag(suggestion.tag)] = suggestion.id
      }
    }
    return map
  }, [suggestions])
  const {
    updateTagColor,
    updateTagName,
    isPending: isTagUpdatePending,
  } = useUpdateOrganizationTagColor()

  const handleTagsChange = useCallback(
    (nextTags: string[]) => {
      onTagsChange(normalizeTags(nextTags, maxTags))
    },
    [maxTags, onTagsChange],
  )

  const handleCreateTag = useCallback(
    (label: string) => {
      const nextTag = normalizeTag(label)
      if (nextTag && !currentTags.includes(nextTag)) {
        handleTagsChange([...currentTags, nextTag])
      }
    },
    [currentTags, handleTagsChange],
  )

  const handleRenameTag = useCallback(
    (tag: string, nextLabel: string) => {
      const nextTag = normalizeTag(nextLabel)
      if (!nextTag || nextTag === tag) {
        return
      }
      updateTagName(tag, nextTag)
      handleTagsChange(
        currentTags.map((currentTag) =>
          currentTag === tag ? nextTag : currentTag,
        ),
      )
    },
    [currentTags, handleTagsChange, updateTagName],
  )

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      handleTagsChange(currentTags.filter((tag) => tag !== tagToRemove))
    },
    [currentTags, handleTagsChange],
  )

  // Every tag the menu can offer: the organization's catalog, plus anything
  // already on this record that the catalog hasn't caught up with.
  const options = useMemo<TagSelectOption[]>(() => {
    const byName = new Map<string, TagSelectOption>()
    for (const suggestion of suggestions) {
      const tag = normalizeTag(suggestion.tag)
      if (tag) {
        byName.set(tag, {
          value: tag,
          label: tag,
          color: suggestion.color ?? getDefaultTagColor(tag),
        })
      }
    }
    for (const tag of currentTags) {
      if (!byName.has(tag)) {
        byName.set(tag, {
          value: tag,
          label: tag,
          color: getDefaultTagColor(tag),
        })
      }
    }
    return Array.from(byName.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    )
  }, [suggestions, currentTags])

  const trackLayer = useCallback((key: string, isOpen: boolean) => {
    setOpenLayerKey((current) => {
      if (isOpen) {
        return key
      }
      return current === key ? null : current
    })
  }, [])

  const renderOptionDot = useCallback(
    (option: TagSelectOption, { color }: { color: string }) => {
      const tag = option.value
      return (
        <TagColorSwatchPicker
          tag={tag}
          color={color}
          disabled={!tagIdsByName[tag]}
          isPending={isTagUpdatePending}
          onColorChange={(nextColor) => updateTagColor(tag, nextColor)}
          onOpenChange={(isOpen) => trackLayer(`color:${tag}`, isOpen)}
        />
      )
    },
    [isTagUpdatePending, tagIdsByName, trackLayer, updateTagColor],
  )

  const renderOptionLabel = useCallback(
    (option: TagSelectOption) => {
      const tag = option.value
      return (
        <TagNameEditor
          tag={tag}
          disabled={!tagIdsByName[tag]}
          isPending={isTagUpdatePending}
          onRename={(nextLabel) => handleRenameTag(tag, nextLabel)}
          onEditingChange={(isEditing) => trackLayer(`name:${tag}`, isEditing)}
        />
      )
    },
    [handleRenameTag, isTagUpdatePending, tagIdsByName, trackLayer],
  )

  const renderTagSelect = (trigger?: React.ReactElement) => (
    <TagSelect
      options={options}
      value={currentTags}
      onValueChange={handleTagsChange}
      variant={triggerVariant}
      triggerLabel={triggerLabel}
      disabled={disabled}
      maxVisible={maxOverflowDots}
      allowCreate={showInput}
      onCreateOption={handleCreateTag}
      maxSelected={maxTags}
      readOnly={!showInput && !allowRemove}
      renderOptionDot={allowColorEdit ? renderOptionDot : undefined}
      renderOptionLabel={allowNameEdit ? renderOptionLabel : undefined}
      keepOpen={openLayerKey !== null}
      trigger={trigger}
    />
  )

  if (collapseOverflow) {
    return (
      <div
        className={`flex min-w-0 items-center justify-end gap-1.5 ${className ?? ""}`}
      >
        {renderTagSelect()}
      </div>
    )
  }

  const visibleTags =
    maxVisibleTags === undefined
      ? currentTags
      : currentTags.slice(0, maxVisibleTags)

  /*
   * Chips already name every visible tag here, so the control is only an
   * "add" affordance — a glyph trigger beside them would repeat the same
   * tags twice over.
   */
  const addTrigger =
    inputVariant === "button" ? (
      <Button
        type="button"
        variant="secondary"
        aria-label="Add tag"
        disabled={disabled}
      >
        Add Tag
      </Button>
    ) : (
      <Button
        type="button"
        icon
        variant="ghost"
        tooltip="Add tag"
        disabled={disabled}
        className={`h-5 w-5 min-w-5 ${inputVariant === "tag" ? "rounded-none" : ""}`}
      >
        {inputVariant === "tag" ? (
          <TagIcon className="h-3 w-3" />
        ) : (
          <Plus className="h-3 w-3" />
        )}
      </Button>
    )

  return (
    <div
      className={`flex min-w-0 items-center gap-1.5 ${
        wrap ? "flex-wrap" : "flex-nowrap overflow-hidden"
      } ${className ?? ""}`}
    >
      {visibleTags.map((tag) => {
        const chip = (
          <TagChip
            key={tag}
            tag={tag}
            color={tagColorMap[tag]}
            onRemove={
              allowRemove && !disabled ? () => handleRemoveTag(tag) : undefined
            }
          />
        )
        return wrap ? (
          chip
        ) : (
          <div key={tag} className="min-w-0 shrink overflow-hidden">
            {chip}
          </div>
        )
      })}
      {showInput
        ? renderTagSelect(addTrigger)
        : visibleTags.length < currentTags.length
          ? renderTagSelect()
          : null}
    </div>
  )
}
